import "./styles.scss";

import Button from "components/ui/button";
import bdaylytics from "components/utils/analytics/bdaylytics";
import { getPrettyDate } from "components/utils/date";
import { useApp, useQueryDict } from "hooks/app";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { FaBirthdayCake, FaTimes } from "react-icons/fa";
import { addBirthdayRequest } from "services/reminder-service";

const ScraperConfirm = ({ onSuccess, marketingId, viralBirthdayRequestId, buttonTitle, importedContacts, existingContacts, errorContacts }) => {
  const { showAlert } = useApp();
  const { search } = useQueryDict();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([...existingContacts, ...importedContacts]);
  const selectedImportedContacts = importedContacts.filter((c) => selectedContacts?.find((sc) => c.contactId === sc.contactId));
  const selectedExistingContacts = existingContacts.filter((c) => selectedContacts?.find((sc) => c.contactId === sc.contactId));

  const sendBirthdayRequests = async () => {
    setIsSubmitting(true);
    const contactIds = [...importedContacts.map((c) => c.contactId), ...existingContacts.map((c) => c.originalContact.contactId)];
    const payload = { contactIds, marketingId, viralBirthdayRequestId };
    const { errors, ...result } = await addBirthdayRequest(payload);
    if (errors?.length > 0) {
      showAlert({ message: errors[0].errorMessage, type: "error" });
    } else {
      if (result.birthdayRequests?.length > 0) {
        const successfulBirthdayRequests = result.birthdayRequests.filter((br) => br.status === "added");
        if (successfulBirthdayRequests?.length > 0) {
          const trackingData = { search, count: successfulBirthdayRequests.length };
          bdaylytics.track("New Birthday Request", trackingData, "SCRAPER", "Key Metrics");
        }
        onSuccess(successfulBirthdayRequests.length);
      } else {
        onSuccess(0);
      }
    }
  };

  const removeSelectedContact = (contactId) => {
    setSelectedContacts(selectedContacts.filter((c) => c.contactId !== contactId));
  };

  return (
    <div className="contacts-scraper-confirm">
      <div className="contacts-container">
        {selectedImportedContacts.length > 0 && (
          <div className="contacts-list">
            <span className="contacts-list-title bold">Added contacts:</span>
            {selectedImportedContacts.map((contact, index) => (
              <div className="contact-item" key={`added_contact_${contact.index + 1}`}>
                <div className="contact-details">
                  <div className="contact-image">
                    <span>{index + 1}</span>
                  </div>
                  <div className="contact-info">
                    <span className="contact-name bold">
                      {contact.firstName} {contact.lastName}
                    </span>
                    <span className="contact-email">{contact.email}</span>
                    <span className="contact-delete" onClick={() => removeSelectedContact(contact.contactId)} role="button" tabIndex="0">
                      <FaTimes />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedExistingContacts.length > 0 && (
          <div className="contacts-list">
            <span className="contacts-list-title bold">Already existing contacts:</span>
            {selectedExistingContacts.map((contact, index) => (
              <div className="contact-item" key={`old_contact_${contact.index + 1}`}>
                <div className="contact-details">
                  <div className="contact-image">
                    <span>{selectedImportedContacts.length + index + 1}</span>
                  </div>
                  <div className="contact-info">
                    <span className="contact-name bold">
                      {contact.firstName} {contact.lastName}
                    </span>
                    <span className="contact-email">{contact.email}</span>
                    {contact.originalContact?.reminders?.find((r) => r.calendarTypeId === 1 && !r.deleteDttm) && (
                      <span className="contact-birthday">
                        <FaBirthdayCake />
                        {getPrettyDate(contact.originalContact.reminders.find((r) => r.calendarTypeId === 1).calendarDttm)}
                      </span>
                    )}
                    <span className="contact-delete" onClick={() => removeSelectedContact(contact.contactId)} role="button" tabIndex="0">
                      <FaTimes />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {errorContacts.length > 0 && (
          <div className="contacts-list">
            <span className="contacts-list-title bold">Invalid Contacts:</span>
            {errorContacts.map((contact, index) => (
              <div className="contact-item disabled" key={`error_contact_${contact.index + 1}`}>
                <div className="contact-details">
                  <div className="contact-image">
                    <span>{selectedImportedContacts.length + selectedExistingContacts.length + index + 1}</span>
                  </div>
                  <div className="contact-info">
                    <span className="contact-name bold">
                      {contact.firstName} {contact.lastName}
                    </span>
                    <span className="contact-email">{contact.email}</span>
                    {contact.errors && contact.errors.length > 0 && (
                      <div className="contact-error-message">
                        <span>{contact.errors[0].errorMessage}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button onClick={sendBirthdayRequests} disabled={isSubmitting || selectedContacts.length === 0}>
        {buttonTitle.replace("{0}", selectedContacts.length)}
      </Button>
      <div className="skip-button">
        <span onClick={() => onSuccess(0)}>skip</span>
      </div>
    </div>
  );
};

ScraperConfirm.propTypes = {
  buttonTitle: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  marketingId: PropTypes.number,
  viralBirthdayRequestId: PropTypes.string,
  importedContacts: PropTypes.array.isRequired,
  existingContacts: PropTypes.array.isRequired,
  errorContacts: PropTypes.array.isRequired,
};

ScraperConfirm.defaultProps = {
  marketingId: null,
  viralBirthdayRequestId: "",
};

export default ScraperConfirm;
